/**
 * Contributed-skills identity — the client mirror of the server gate
 * `isContributedSkillsKey` (github-proxy/src/download.ts), following the same
 * read-time membership-check convention as `official.ts` (identity compared at call
 * time, nothing persisted on the entry). Unlike official.ts — which matches against a
 * curated allowlist — this derives the target repo identity from config.
 *
 * The web app's only write path (github-proxy) is hardcoded to the contributed-skills
 * repo, so Edit/Delete are legitimate ONLY for records that actually live there. A
 * record's identity (source + org/repo) is compared case-insensitively against the
 * write-path repo derived from `REACT_APP_CONTRIBUTED_SKILLS_REPO_URL`. Contributed
 * skills are always GHES (the hardcoded source in the proxy's sqs-notify.ts, and this
 * module's own `CONTRIBUTED_SKILLS_SOURCE`), so a non-GHES record is never contributed.
 *
 * Fail-closed caveat (issue #404): if the URL is empty or unparseable, owner/repo
 * resolve to "" and every record returns `false` — Edit/Delete vanish for everyone and
 * the edit route errors. env.ts ships a hardcoded default so a missing build var can't
 * trigger this, and the deploy scripts also export the var explicitly. If it is empty
 * or unparseable anyway, `warnMisconfiguredOnce` surfaces the misconfiguration once so
 * it isn't silent.
 */

import type { MarketplaceEntry } from "../types/index";
import { env } from "./env";
import { reportError } from "./newrelic";

const CONTRIBUTED_SKILLS_SOURCE = "ghes";

/** Parse `owner`/`repo` from a git URL like `https://host/owner/repo.git`. */
function parseOwnerRepo(url: string): { owner: string; repo: string } {
  try {
    const { pathname } = new URL(url);
    const parts = pathname.replace(/\.git$/, "").split("/").filter(Boolean);
    if (parts.length < 2) return { owner: "", repo: "" };
    return { owner: parts[parts.length - 2], repo: parts[parts.length - 1] };
  } catch {
    return { owner: "", repo: "" };
  }
}

// Emitted at most once per session: a truthy-but-unparseable (or empty) repo URL fails
// closed for EVERY record, which otherwise looks like "nothing is contributed" with no
// signal. Route through reportError (not a bare console.error) so a prod misconfig
// reaches on-call: the New Relic browser agent does NOT capture console.error — only
// uncaught errors and noticeError count toward the JavaScriptError alert (see
// reportError in lib/newrelic.ts; same convention as AppContent's edit-load handler).
// Keep the console.error too for local dev, where reportError no-ops (agent uninitialized).
let misconfigWarned = false;
function warnMisconfiguredOnce(): void {
  if (misconfigWarned) return;
  misconfigWarned = true;
  const message =
    "[contributed] REACT_APP_CONTRIBUTED_SKILLS_REPO_URL is empty or unparseable — " +
    "Edit/Delete will be hidden for every record and the edit route will error. " +
    "Set this build var (see scripts/deploy-web-full.sh).";
  console.error(message);
  reportError(new Error(message), "contributed-repo-misconfigured");
}

/** True when the entry lives in the contributed-skills write-path repo. */
export function isContributedRecord(
  p: Pick<MarketplaceEntry, "source" | "orgOrOwner" | "repoName"> | null | undefined,
): boolean {
  if (!p) return false;
  const { owner, repo } = parseOwnerRepo(env.REACT_APP_CONTRIBUTED_SKILLS_REPO_URL);
  if (!owner || !repo) {
    warnMisconfiguredOnce();
    return false;
  }
  return (
    p.source === CONTRIBUTED_SKILLS_SOURCE &&
    p.orgOrOwner.toLowerCase() === owner.toLowerCase() &&
    p.repoName.toLowerCase() === repo.toLowerCase()
  );
}
